// src/App.js
// High-Performance App Component with Route-Level Code Splitting

import React, { useState, useEffect, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { I18nextProvider } from 'react-i18next';
import i18n from './i18n/index';
import PWAInstaller from './components/PWAInstaller';
import { registerServiceWorker } from './utils/pwaUtils';
import { initEnhancedAnalytics, trackPageView, trackSEOMetrics } from './utils/analytics';
import { LazyLoader } from './components/Common/LazyLoader';
import { initPerformanceOptimizations } from './utils/performanceUtils';
import { initAdvancedPreloading } from './utils/advancedPreloading';
import { initAdvancedCaching } from './utils/advancedCaching';
import './App.css';

// Critical components loaded immediately (above the fold)
import Header from './components/Layout/Header/Header';
import Footer from './components/Layout/Footer/Footer';
import ScrollToTop from './components/Common/ScrollToTop';
import ContactWidget from './components/ContactWidget';
import CookieConsent from './components/Common/CookieConsent/CookieConsent';

// Route-Level Code Splitting - Lazy loaded components
const HomePage = lazy(() => 
  import('./pages/Home')
    .then(module => {
      console.log('HomePage loaded successfully');
      return module;
    })
    .catch(error => {
      console.error('HomePage loading error:', error);
      // Return a fallback component
      return {
        default: () => (
          <div style={{ padding: '40px', textAlign: 'center' }}>
            <h2>Loading Error</h2>
            <p>There was an issue loading the homepage. Please refresh the page.</p>
            <button onClick={() => window.location.reload()}>Refresh Page</button>
          </div>
        )
      };
    })
);

const AboutPage = lazy(() => 
  import('./pages/About').then(module => {
    console.log('AboutPage loaded');
    return module;
  })
);

const ConsultancyServices = lazy(() => 
  import('./pages/ConsultancyServices').then(module => {
    console.log('ConsultancyServices loaded');
    return module;
  })
);

// Service Pages - Lazy loaded with preloading strategy
const FSSC = lazy(() => import('./pages/ConsultancyServices/ServicePages/FSSC'));
const ISO22000 = lazy(() => import('./pages/ConsultancyServices/ServicePages/ISO22000'));
const HACCP = lazy(() => import('./pages/ConsultancyServices/ServicePages/HACCP'));
const GMP = lazy(() => import('./pages/ConsultancyServices/ServicePages/GMP'));
const ISO9001 = lazy(() => import('./pages/ConsultancyServices/ServicePages/ISO9001'));
const GDPMD = lazy(() => import('./pages/ConsultancyServices/ServicePages/GDPMD'));
const GMPPlus = lazy(() => import('./pages/ConsultancyServices/ServicePages/GMPPlus'));

// Other Pages
const ContactPage = lazy(() => import('./pages/Contact'));
const GalleryPage = lazy(() => import('./pages/Gallery'));
const BlogPage = lazy(() => import('./pages/Blog'));

// Blog Posts
const NyahbekuPage = lazy(() => import('./pages/Blog/posts/Nyahbeku'));
const ToiletPage = lazy(() => import('./pages/Blog/posts/Toilet'));
const WhatGMPPage = lazy(() => import('./pages/Blog/posts/WhatGMP'));
const KLPJPPage = lazy(() => import('./pages/Blog/posts/KLPJ'));

// Training Pages
const ManagementSystemTraining = lazy(() => import('./pages/Training/TrainingPages/ManagementSystemTraining'));

// Error Page
const NotFoundPage = lazy(() => import('./pages/NotFound'));

// Enhanced Loading Component with ADTAC Branding
const GlobalLoadingSpinner = () => (
  <div className="global-loading-spinner" style={{
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(135deg, #000000 0%, #1a1a1a 100%)',
    color: '#ffffff',
    zIndex: 9999,
    flexDirection: 'column'
  }}>
    <div style={{
      width: '80px',
      height: '80px',
      border: '4px solid #333',
      borderTop: '4px solid #D40523',
      borderRadius: '50%',
      animation: 'spin 1.2s cubic-bezier(0.4, 0, 0.2, 1) infinite',
      marginBottom: '30px'
    }} />
    
    <div style={{
      fontFamily: "'Exo', sans-serif",
      fontSize: '18px',
      fontWeight: '600',
      marginBottom: '10px',
      letterSpacing: '1px'
    }}>
      LOADING
    </div>
    
    <div style={{
      fontFamily: "'Open Sans', sans-serif",
      fontSize: '14px',
      color: '#D40523',
      fontWeight: '400'
    }}>
      Food Safety Excellence
    </div>

    <div style={{
      width: '200px',
      height: '2px',
      background: '#333',
      borderRadius: '1px',
      marginTop: '30px',
      overflow: 'hidden'
    }}>
      <div style={{
        width: '100%',
        height: '100%',
        background: 'linear-gradient(90deg, #D40523, #ff6b6b)',
        animation: 'progressBar 2s ease-in-out infinite'
      }} />
    </div>
  </div>
);

// Enhanced Analytics Component with Performance Tracking
function EnhancedAnalytics() {
  const location = useLocation();

  useEffect(() => {
    // Initialize enhanced analytics on app start
    initEnhancedAnalytics();
    
    // Track initial UTM parameters
    trackSEOMetrics();
    
    // Track Core Web Vitals
    if ('web-vitals' in window) {
      import('web-vitals').then(({ getCLS, getFID, getFCP, getLCP, getTTFB }) => {
        getCLS(console.log);
        getFID(console.log);
        getFCP(console.log);
        getLCP(console.log);
        getTTFB(console.log);
      });
    }
  }, []);

  useEffect(() => {
    // Performance tracking for route changes
    const navigationStart = performance.now();
    
    // Track page views on route changes with enhanced data
    const pageTitle = document.title;
    trackPageView(location.pathname + location.search, pageTitle);
    
    // Track UTM parameters for each page if present
    const urlParams = new URLSearchParams(location.search);
    if (urlParams.has('utm_source') || urlParams.has('utm_medium') || urlParams.has('utm_campaign')) {
      trackSEOMetrics(
        urlParams.get('utm_source'),
        urlParams.get('utm_medium'),
        urlParams.get('utm_campaign')
      );
    }
    
    // Track navigation performance
    setTimeout(() => {
      const navigationTime = performance.now() - navigationStart;
      console.log(`Navigation to ${location.pathname} took ${navigationTime}ms`);
    }, 0);
    
  }, [location]);

  return null;
}

// Route preloading strategy
const preloadCriticalRoutes = () => {
  setTimeout(() => {
    import('./pages/ConsultancyServices');
    import('./pages/Contact');
  }, 2000);
  
  setTimeout(() => {
    import('./pages/ConsultancyServices/ServicePages/ISO22000');
    import('./pages/ConsultancyServices/ServicePages/HACCP');
    import('./pages/ConsultancyServices/ServicePages/FSSC');
  }, 5000);
};

function App() {
  const [isAppReady, setIsAppReady] = useState(false);
  
  useEffect(() => {
    // Performance optimized app initialization
    const initApp = async () => {
      try {
        // Register service worker for caching
        await registerServiceWorker();
        
        // Initialize performance monitoring
        initPerformanceOptimizations();
        
        // Initialize advanced preloading
        initAdvancedPreloading();
        
        // Initialize advanced caching
        initAdvancedCaching();
        
        // Add performance-critical meta tags
        const addMetaTags = () => {
          const metaTags = [
            // Core Web Vitals optimization
            { name: 'viewport', content: 'width=device-width, initial-scale=1, shrink-to-fit=no' },
            
            // PWA Meta Tags
            { name: 'apple-mobile-web-app-capable', content: 'yes' },
            { name: 'apple-mobile-web-app-status-bar-style', content: 'black-translucent' },
            { name: 'apple-mobile-web-app-title', content: 'ADTAC' },
            { name: 'theme-color', content: '#D40523' },
            
            // Performance hints
            { name: 'format-detection', content: 'telephone=no' },
            { name: 'msapplication-tap-highlight', content: 'no' },
            
            // Enhanced geo meta tags for Malaysian SEO
            { name: 'geo.region', content: 'MY-10' },
            { name: 'geo.placename', content: 'Selangor' },
            { name: 'geo.position', content: '3.0738;101.5183' },
            { name: 'ICBM', content: '3.0738,101.5183' }
          ];
          
          metaTags.forEach(meta => {
            let existingMeta = document.querySelector(`meta[name="${meta.name}"]`);
            if (!existingMeta) {
              existingMeta = document.createElement('meta');
              existingMeta.name = meta.name;
              document.head.appendChild(existingMeta);
            }
            existingMeta.content = meta.content;
          });
        };
        
        // Add link tags for performance
        const addLinkTags = () => {
          // Apple touch icon
          if (!document.querySelector('link[rel="apple-touch-icon"]')) {
            const appleTouchIcon = document.createElement('link');
            appleTouchIcon.rel = 'apple-touch-icon';
            appleTouchIcon.href = '/assets/images/logo/adtac-logo-180.png';
            document.head.appendChild(appleTouchIcon);
          }
          
          // DNS prefetch for external resources
          const dnsPrefetch = [
            'https://fonts.googleapis.com',
            'https://fonts.gstatic.com',
            'https://www.googletagmanager.com',
            'https://www.google-analytics.com'
          ];
          
          dnsPrefetch.forEach(domain => {
            if (!document.querySelector(`link[rel="dns-prefetch"][href="${domain}"]`)) {
              const link = document.createElement('link');
              link.rel = 'dns-prefetch';
              link.href = domain;
              document.head.appendChild(link);
            }
          });
        };
        
        addMetaTags();
        addLinkTags();
        
        // Start route preloading strategy
        preloadCriticalRoutes();
        
        // Mark app as ready
        setIsAppReady(true);
        
      } catch (error) {
        console.error('App initialization error:', error);
        setIsAppReady(true); // Still show app even if some optimizations fail
      }
    };
    
    initApp();
  }, []);
  
  // Show loading screen while app initializes
  if (!isAppReady) {
    return <GlobalLoadingSpinner />;
  }
  
  return (
    <I18nextProvider i18n={i18n}>
      <HelmetProvider>
        <Router>
          <ScrollToTop />
          <EnhancedAnalytics />
          <div className="App">
            <Header />
            
            <main className="main-content">
              <Routes>
                {/* Main Routes with Lazy Loading */}
                <Route 
                  path="/" 
                  element={
                    <LazyLoader loadingMessage="Loading Home..." componentName="home">
                      <HomePage />
                    </LazyLoader>
                  } 
                />
                <Route 
                  path="/about-us" 
                  element={
                    <LazyLoader loadingMessage="Loading About Us..." componentName="about">
                      <AboutPage />
                    </LazyLoader>
                  } 
                />
                <Route 
                  path="/consultancy-services" 
                  element={
                    <LazyLoader loadingMessage="Loading Consultancy Services..." componentName="consultancy">
                      <ConsultancyServices />
                    </LazyLoader>
                  } 
                />
                <Route 
                  path="/management-system-training" 
                  element={
                    <LazyLoader loadingMessage="Loading Training..." componentName="training">
                      <ManagementSystemTraining />
                    </LazyLoader>
                  } 
                />
                <Route 
                  path="/gallery" 
                  element={
                    <LazyLoader loadingMessage="Loading Gallery..." componentName="gallery">
                      <GalleryPage />
                    </LazyLoader>
                  } 
                />
                <Route 
                  path="/blog" 
                  element={
                    <LazyLoader loadingMessage="Loading Blog..." componentName="blog">
                      <BlogPage />
                    </LazyLoader>
                  } 
                />
                <Route 
                  path="/contact" 
                  element={
                    <LazyLoader loadingMessage="Loading Contact..." componentName="contact">
                      <ContactPage />
                    </LazyLoader>
                  } 
                />
                
                {/* Certification Service Routes with Optimized Loading */}
                <Route 
                  path="/consultancy-services/fssc" 
                  element={
                    <LazyLoader loadingMessage="Loading FSSC 22000..." componentName="fssc">
                      <FSSC />
                    </LazyLoader>
                  } 
                />
                <Route 
                  path="/consultancy-services/iso-22000" 
                  element={
                    <LazyLoader loadingMessage="Loading ISO 22000..." componentName="iso22000">
                      <ISO22000 />
                    </LazyLoader>
                  } 
                />
                <Route 
                  path="/consultancy-services/iso-9001" 
                  element={
                    <LazyLoader loadingMessage="Loading ISO 9001..." componentName="iso9001">
                      <ISO9001 />
                    </LazyLoader>
                  } 
                />
                <Route 
                  path="/consultancy-services/haccp" 
                  element={
                    <LazyLoader loadingMessage="Loading HACCP..." componentName="haccp">
                      <HACCP />
                    </LazyLoader>
                  } 
                />
                <Route 
                  path="/consultancy-services/gmp" 
                  element={
                    <LazyLoader loadingMessage="Loading GMP..." componentName="gmp">
                      <GMP />
                    </LazyLoader>
                  } 
                />
                <Route 
                  path="/consultancy-services/gdpmd" 
                  element={
                    <LazyLoader loadingMessage="Loading GDPMD..." componentName="gdpmd">
                      <GDPMD />
                    </LazyLoader>
                  } 
                />
                <Route 
                  path="/consultancy-services/gmp-plus" 
                  element={
                    <LazyLoader loadingMessage="Loading GMP+..." componentName="gmpplus">
                      <GMPPlus />
                    </LazyLoader>
                  } 
                />
                
                {/* Blog Post Routes */}
                <Route 
                  path="/blog/kursus-latihan-pengendalian-makanan-kuala-lumpur-petaling-jaya-shah-alam"
                  element={
                    <LazyLoader loadingMessage="Loading Article..." componentName="blog-post">
                      <KLPJPPage />
                    </LazyLoader>
                  } 
                />
                <Route 
                  path="/blog/nyahbeku-dengan-rendam-makanan-dalam-air" 
                  element={
                    <LazyLoader loadingMessage="Loading Article..." componentName="blog-post">
                      <NyahbekuPage />
                    </LazyLoader>
                  } 
                />
                <Route 
                  path="/blog/toilet-gmp-apa-kaitannya" 
                  element={
                    <LazyLoader loadingMessage="Loading Article..." componentName="blog-post">
                      <ToiletPage />
                    </LazyLoader>
                  } 
                />
                <Route 
                  path="/blog/apa-itu-gmp" 
                  element={
                    <LazyLoader loadingMessage="Loading Article..." componentName="blog-post">
                      <WhatGMPPage />
                    </LazyLoader>
                  } 
                />
                
                {/* 404 Route */}
                <Route 
                  path="*" 
                  element={
                    <LazyLoader loadingMessage="Loading..." componentName="404">
                      <NotFoundPage />
                    </LazyLoader>
                  } 
                />
              </Routes>
            </main>
            
            <Footer />

            {/* PWA Installation Component */}
            <PWAInstaller />
            <ContactWidget />
            
            {/* Cookie Consent Banner */}
            <CookieConsent />
          </div>
        </Router>
      </HelmetProvider>
    </I18nextProvider>
  );
}

export default App;